import { create } from 'zustand';

import type { TypeSmartSearchFeedbackState, TypeSmartSearchMode } from '~/types/smart-search.types';

type TypeSmartSearchStore = TypeSmartSearchStoreState & { actions: TypeSmartSearchStoreActions };

type TypeSmartSearchStoreState = {
  /** 패널 모드 */
  mode: TypeSmartSearchMode;
  /** 최소화 이전 모드 (복원용) */
  prevMode: TypeSmartSearchMode;
  /** 현재 검색어 (입력 중) */
  inputValue: string;
  /** 실행된 검색어 (API 호출용) */
  submittedQuery: string;
  /** 피드백 상태 */
  feedbackState: TypeSmartSearchFeedbackState;
  /** 피드백 토스트 표시 여부 (검색 결과당 1회) */
  hasShownFeedbackToast: boolean;
};

type TypeSmartSearchStoreActions = {
  /** 모드 전환. minimized 전환 시 이전 모드 자동 보관 */
  setMode: (mode: TypeSmartSearchMode) => void;
  /** 미니바 → 이전 모드 복원 */
  restoreFromMinibar: () => void;

  setInputValue: (value: string) => void;
  submitSearch: (query: string) => void;
  setFeedbackState: (state: TypeSmartSearchFeedbackState) => void;
  /** 스토어 상태 초기화 */
  resetStore: (keys?: Array<keyof TypeSmartSearchStoreState>) => void;
};

export const initialSmartSearchStoreValue: TypeSmartSearchStoreState = {
  mode: 'closed',
  prevMode: 'docked',
  inputValue: '',
  submittedQuery: '',
  feedbackState: 'none',
  hasShownFeedbackToast: false,
};

/**
 * [스마트검색] 공통 스토어
 */
export const useSmartSearchStore = create<TypeSmartSearchStore>((set, get) => ({
  ...initialSmartSearchStoreValue,

  actions: {
    setMode: (newMode) => {
      const { mode } = get();
      set({
        mode: newMode,
        ...(newMode === 'minimized' && {
          prevMode: mode === 'docked' ? 'docked' : 'floating',
        }),
      });
    },

    restoreFromMinibar: () => set({ mode: get().prevMode }),

    setInputValue: (value) => set({ inputValue: value }),

    submitSearch: (query) =>
      set({ submittedQuery: query, inputValue: query, feedbackState: 'none', hasShownFeedbackToast: false }),

    setFeedbackState: (state) => set({ feedbackState: state }),

    resetStore: (keys?) => {
      if (!keys) {
        set(initialSmartSearchStoreValue);
        return;
      }
      keys.forEach((key) => {
        set({ [key]: initialSmartSearchStoreValue[key] });
      });
    },
  },
}));
